"use client";

import { t } from "@/content/i18n/messages";
import { EzyChatButton } from "@/components/shared/EzyChatCard";
import { MessageCircle, ArrowUpRight } from "lucide-react";
import { ezychatDemoMessage, whatsappHref, WHATSAPP_NUMBER } from "@/lib/whatsapp";
import type { Locale } from "@/lib/i18n";

/**
 * Hook card for EzyChat, our own free open-source WhatsApp team inbox
 * (approved by Jazz 2026-10-11). Sits directly BELOW the hero, never inside it,
 * and stays smaller than the hero CTAs so it doesn't compete with them.
 *
 * Speed rules (see #431): no entrance animation and no opacity:0, so the card
 * is in the static HTML. The logo is lazy, small and has fixed dimensions, so it
 * can neither shift layout nor become the LCP element. Target: <= ~260px
 * tall at 360px wide.
 *
 * The button + "Built by Anchor Sprint" line are shared with the compact
 * content-page card in @/components/shared/EzyChatCard (same markup).
 *
 * Claims must match ezychat.ai: free, open source (MIT), runs on your own
 * computer, AI sales agent is optional. The logo is EzyChat's own asset
 * (ezychat.ai/brand/ezychat-logo.png), resized to WebP, not redrawn.
 */
export default function EzyChatHookCard({ locale }: { locale: Locale }) {
  return (
    <section className="bg-background" aria-labelledby="ezychat-hook-heading">
      <div className="mx-auto max-w-7xl px-4 pt-10 pb-12 sm:px-6 lg:px-8 lg:pt-12 lg:pb-16">
        <div className="relative overflow-hidden rounded-2xl border border-border bg-card p-4 sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-8">
            <div>
              <div className="flex items-center gap-3">
                <img
                  src="/images/ezychat/ezychat-logo.webp"
                  alt="EzyChat"
                  width={36}
                  height={35}
                  loading="lazy"
                  decoding="async"
                  className="h-[35px] w-9 shrink-0 rounded-lg"
                />
                <h2
                  id="ezychat-hook-heading"
                  className="font-display text-lg font-bold leading-tight text-neutral-emphasis sm:text-xl"
                >
                  {t(locale, "Drowning in WhatsApp chats?")}
                </h2>
              </div>
              <p className="mt-2 text-sm font-medium leading-snug text-neutral-emphasis">
                {t(locale, "Too many chats, too many staff, messages missed.")}
              </p>
              <p className="mt-1 max-w-2xl text-sm leading-snug text-neutral-muted">
                {t(
                  locale,
                  "EzyChat puts your team on one shared WhatsApp inbox, with an optional AI sales agent that replies for you. Free and open-source, runs on your own computer.",
                )}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:shrink-0 sm:flex-col sm:items-end sm:gap-2">
              <EzyChatButton locale={locale} location="home_card" />
              <a
                href={whatsappHref(ezychatDemoMessage("your homepage"))}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t(locale, "Try our EzyChat AI assistant on WhatsApp")}
                data-track-event="contact_whatsapp_click"
                data-track-location="home_ezychat_whatsapp_demo"
                data-track-action="ezychat_ai_demo"
                data-track-phone={WHATSAPP_NUMBER}
                className="inline-flex w-full items-center gap-1.5 rounded-sm py-2 text-sm font-medium text-neutral-muted underline decoration-border underline-offset-4 transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:w-auto"
              >
                <MessageCircle className="h-4 w-4 shrink-0" aria-hidden />
                {t(locale, "Try the WhatsApp AI assistant")}
                <ArrowUpRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
